import React, { useState, useEffect, useMemo } from 'react';
import { Form, Input, Select, Table, Row, Col, Spin, Typography, message, Button } from 'antd';
import axiosInstance from '../../../axiosInstance';
import { useParams } from 'react-router-dom';
import generateReceipt from './reports/sales';
import { getPermission } from '../../constants';

const { Option } = Select;
const { Title } = Typography;

const SaleView = () => {
    const [form] = Form.useForm();
    const [saleDetails, setSaleDetails] = useState(null);
    const [items, setItems] = useState([]);
    const [fetching, setFetching] = useState(false);
    const { id } = useParams();
    const [status, setStatus] = useState('drafted');
    const [statusdb, setStatusdb] = useState('');
    const [paymentDate, setPaymentDate] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [remainingpaymentAmount, setRemainingPaymentAmount] = useState(parseFloat(saleDetails?.remaining_amount || 0));
    const [accountType, setAccountType] = useState(null);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [filteredAccounts, setFilteredAccounts] = useState([]);
    const [reverseReason, setReverseReason] = useState(saleDetails?.reverse_reason || null);
    const userPermission = getPermission();

    const statuses = ['drafted', 'processed', 'reversed', 'paid', 'canceled'];
    const filteredStatuses = userPermission?.is_reverse
        ? [...statuses] // If the user has approval permission, show all statuses including 'approved'
        : statuses.filter(status => status !== 'reversed');

    useEffect(() => {
        const fetchSaleDetails = async () => {
            try {
                setFetching(true);
                const response = await axiosInstance.get(`/sales/${id}`);
                const { data } = response;
                setSaleDetails(data);
                setItems(data.items);
                setStatus(data.status);
                setStatusdb(data.status);
                setPaymentDate(data.payment_date || null);
            } catch (error) {
                message.error('Failed to fetch sale details.');
            } finally {
                setFetching(false);
            }
        };

        const fetchAccounts = async () => {
            try {
                const response = await axiosInstance.get('/account');
                setAccounts(response.data);
            } catch (error) {
                message.error('Failed to fetch accounts.');
            }
        };

        fetchSaleDetails();
        fetchAccounts();
    }, [id]);



    const handleStatusChange = async () => {
        try {
            setFetching(true);
            const payload = { status };

            if (status === 'paid' || status === 'processed') {
                if (!paymentDate || !accountType || !selectedAccount) {
                    message.error('Please provide payment date, account type, and account.');
                    return;
                }
                payload.payment_date = paymentDate;
                payload.account_type = accountType;
                payload.account = selectedAccount;
                payload.paid_amount = paymentAmount || null;
            } else if ((status === 'reversed' && statusdb === 'paid') || (status === 'reversed' && saleDetails?.type === 'invoice')) {
                if (!reverseReason) {
                    message.error('Please select a reason for reversing the transaction.');
                    return;
                }
                payload.reverse_reason = reverseReason;
            } else if (status === 'reversed' && statusdb === 'paid') {
                // Additional logic for reversing
                message.info('Reversing the payment...');
            }

            const response = await axiosInstance.post(`/sales/${id}/update-status`, payload);
            message.success(response.data.message);
            window.location.reload();
        } catch (error) {
            message.error('Failed to update status.');
        } finally {
            setFetching(false);
        }
    };

    const handlePrintReceipt = () => {
        generateReceipt(saleDetails, items, totalQuantity, totalPrice, totalDiscount);
    };

    const filterAccountsByType = (type) => {
        setFilteredAccounts(accounts.filter((account) => account.type === type));
        setAccountType(type);
        setSelectedAccount(null); // Reset selected account when account type changes
    };


    const itemColumns = [
        {
            title: 'Product',
            dataIndex: 'product',
            render: (_, record) => <Input value={record.product?.name} disabled />,
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            render: (quantity) => <Input value={quantity} disabled />,
        },
        {
            title: 'Price',
            dataIndex: 'price',
            render: (price) => <Input value={price} disabled />,
        },
        {
            title: 'Discount',
            dataIndex: 'discount',
            render: (discount) => <Input value={discount} disabled />,
        },
        {
            title: 'Total',
            render: (_, record) => (((record.quantity || 0) * (record.price || 0)) - parseFloat(record.discount || 0)).toFixed(2),
        },
    ];

    const { totalQuantity, totalPrice, totalDiscount } = useMemo(() => {
        let totalQty = 0;
        let totalPrc = 0;
        let totalDisc = 0;
        items.forEach(({ quantity, price, discount }) => {
            const productDiscount = parseFloat(discount) || 0;
            totalQty += quantity || 0;
            totalPrc += ((quantity || 0) * (price || 0)) - productDiscount;
            totalDisc += productDiscount;
        });

        totalDisc = totalDisc + parseFloat(saleDetails?.discount || 0);
        return { totalQuantity: totalQty, totalPrice: totalPrc.toFixed(2), totalDiscount: totalDisc };
    }, [items]);


    useEffect(() => {
        const remaining = saleDetails?.remaining_amount ?? (totalPrice - totalDiscount);
        setRemainingPaymentAmount(remaining);
    }, [saleDetails, totalPrice, totalDiscount]);


    const handleAmountChange = (e) => {
        const value = parseFloat(e.target.value) || 0;
        setPaymentAmount(value);

        const remainingAmount = saleDetails?.remaining_amount ?? (totalPrice - totalDiscount);
        const updatedRemaining = remainingAmount - value;

        // setRemainingPaymentAmount(Math.max(0, updatedRemaining)); // Prevent negative values
        setRemainingPaymentAmount(updatedRemaining);
        // console.log(remainingpaymentAmount);
    };


    return (
        <Spin spinning={fetching} tip="Loading...">
            <div>
                <Title level={3}>Sale Details</Title>

                <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
                    <Col xs={24} sm={12} md={6}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <Select
                                value={status}
                                onChange={setStatus}
                                style={{ width: 200 }}
                                disabled={statusdb === 'reversed'} // Disable dropdown when statusdb is 'reversed'
                            >
                                {filteredStatuses.map((statusOption) => (
                                    <Option
                                        key={statusOption}
                                        value={statusOption}
                                        disabled={statusdb === 'paid' && statusOption !== 'reversed'} // Allow only 'reversed' when statusdb is 'paid'
                                    >
                                        {statusOption.charAt(0).toUpperCase() + statusOption.slice(1)}
                                    </Option>
                                ))}
                            </Select>
                            <Button
                                type="primary"
                                onClick={handleStatusChange}
                                disabled={
                                    fetching ||
                                    (statusdb === 'reversed') || // Disable button if statusdb is 'reversed'
                                    (statusdb === 'paid' && status !== 'reversed') // Disable button until 'reversed' is selected
                                }
                            >
                                Update Status
                            </Button>
                        </div>
                    </Col>
                    {((status === 'paid' && statusdb !== 'paid') || status === 'processed' || statusdb === 'processed') && (
                        <>
                            <Col xs={24} sm={12} md={4}>
                                <Form.Item
                                    label="Payment Date"
                                    name="paymentDate"
                                    rules={[{ required: true, message: 'Please select the payment date' }]}
                                >
                                    <Input
                                        type="date"
                                        value={paymentDate}
                                        onChange={(e) => setPaymentDate(e.target.value)}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={4}>
                                <Form.Item
                                    label="Account Type"
                                    name="accountType"
                                    rules={[{ required: true, message: 'Please select the account type' }]}
                                >
                                    <Select
                                        placeholder="Select Account Type"
                                        onChange={filterAccountsByType}
                                    >
                                        <Option value="online">Online</Option>
                                        <Option value="cash">Cash</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={4}>
                                <Form.Item
                                    label="Account Name"
                                    name="account"
                                    rules={[{ required: true, message: 'Please select an account' }]}
                                >
                                    <Select
                                        placeholder="Select Account"
                                        value={selectedAccount}
                                        onChange={(value) => setSelectedAccount(value)}
                                    >
                                        {filteredAccounts.map((account) => (
                                            <Option key={account.id} value={account.id}>
                                                {account.account_name}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            {(status === 'processed' || statusdb === 'processed') && (
                                <Col xs={24} sm={12} md={4}>
                                    <Form.Item
                                        label="Paid Amount"
                                        name="amount"
                                        rules={[{ required: true, message: 'Please Enter Amount' }]}
                                    >
                                        <Input
                                            type="number"
                                            value={paymentAmount}
                                            onChange={handleAmountChange}
                                            min={0}
                                            placeholder="Enter Paid Amount"
                                            style={{ fontWeight: 'bold', color: 'black' }}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                            {(status === 'processed' || statusdb === 'processed') && (
                                <Col>
                                    <strong>Remaining Amount:</strong> {remainingpaymentAmount || 0}
                                </Col>
                            )}

                        </>
                    )}

                    {status === 'reversed' && (
                        saleDetails?.reverse_reason ? (
                            <Col>
                                <strong>Reason:</strong> {saleDetails.reverse_reason}
                            </Col>
                        ) : (
                            <Col xs={24} sm={12} md={6}>
                                <Form.Item
                                    label="Reason for Reversing"
                                    name="reverseReason"
                                    rules={[{ required: true, message: 'Please select a reason' }]}
                                >
                                    <Select
                                        placeholder="Select a reason"
                                        value={reverseReason}
                                        onChange={setReverseReason}
                                    >
                                        <Option value="Transaction Error | Hitilafu ya Muamala">Transaction Error | Hitilafu ya Muamala</Option>
                                        <Option value="Fraudulent Activity | Shughuli ya Udanganyifu">Fraudulent Activity | Shughuli ya Udanganyifu</Option>
                                        <Option value="Customer Request | Ombi la Mteja">Customer Request | Ombi la Mteja</Option>
                                        <Option value="Other | Nyingine">Other | Nyingine</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        )
                    )}



                    <Col style={{ textAlign: 'right' }}>
                        <Button type="primary" onClick={handlePrintReceipt}>
                            {saleDetails?.type === 'invoice' ? 'Print Invoice' : 'Print Receipt'}
                        </Button>
                    </Col>
                </Row>
                <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Type">
                            <Input value={saleDetails?.type} disabled style={{ color: '#000' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item label="REF ID">
                            <Input value={saleDetails?.invoice_number || saleDetails?.receipt_number} disabled style={{ color: '#000' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Customer Name">
                            <Input value={saleDetails?.customer?.name} disabled style={{ color: '#000' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Account">
                            <Input value={saleDetails?.account?.account_name} disabled style={{ color: '#000' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Pay|Due Date">
                            <Input value={saleDetails?.type === 'invoice' ? saleDetails?.due_date : saleDetails?.payment_date} disabled style={{ color: '#000' }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Table
                    dataSource={items}
                    columns={itemColumns}
                    pagination={false}
                    rowKey={(record, index) => index}
                    bordered
                    scroll={{ x: 'max-content' }}
                />

                <Row justify="space-between" style={{ marginTop: 20 }}>
                    <Col>
                        <strong>Total Quantity:</strong> {totalQuantity}
                    </Col>
                    <Col>
                        <strong>Total Price:</strong> {totalPrice - parseFloat(saleDetails?.discount || 0)}
                    </Col>
                    <Col>
                        <strong>Total Discount:</strong> {totalDiscount}
                    </Col>
                </Row>
            </div>
        </Spin>
    );
};

export default SaleView;
